import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, Shield, Edit2, Plus, X, Check } from 'lucide-react';
import api from '../../services/api';
import { OBRAS, fmt } from './insumos.utils';

function AlertaRow({ alerta, onEdit }) {
  const pct = alerta.estoque_minimo > 0 ? Math.min(100, Math.round((alerta.saldo / alerta.estoque_minimo) * 100)) : 0;
  const deficit = parseFloat(alerta.estoque_minimo) - parseFloat(alerta.saldo);

  return (
    <tr className={`border-b border-gray-50 ${alerta.critico ? 'bg-red-50' : 'bg-yellow-50'}`}>
      <td className="px-4 py-3">
        <div className="flex items-center gap-2">
          {alerta.critico && <Shield size={14} className="text-red-500 shrink-0" />}
          <div>
            <p className="font-medium text-gray-800 text-sm">{alerta.material_nome}</p>
            <p className="text-xs text-gray-400">{alerta.codigo} · {alerta.categoria_nome}</p>
          </div>
        </div>
      </td>
      <td className="px-4 py-3 text-sm text-gray-600">{alerta.obra}</td>
      <td className="px-4 py-3 text-right">
        <span className={`font-mono text-sm font-bold ${parseFloat(alerta.saldo) <= 0 ? 'text-red-600' : 'text-orange-600'}`}>
          {fmt(alerta.saldo, 2)} {alerta.unidade_sigla}
        </span>
      </td>
      <td className="px-4 py-3 text-right font-mono text-sm text-gray-500">{fmt(alerta.estoque_minimo, 2)}</td>
      <td className="px-4 py-3">
        <div className="flex items-center gap-2">
          <div className="flex-1 bg-gray-200 rounded-full h-2 min-w-16">
            <div className={`h-2 rounded-full transition-all ${pct < 25 ? 'bg-red-500' : pct < 60 ? 'bg-yellow-500' : 'bg-green-500'}`} style={{ width: `${pct}%` }} />
          </div>
          <span className="text-xs text-gray-500 w-8 text-right">{pct}%</span>
        </div>
      </td>
      <td className="px-4 py-3 text-right">
        <span className={`text-sm font-medium ${parseFloat(alerta.saldo) <= 0 ? 'text-red-700' : 'text-orange-600'}`}>
          -{fmt(deficit, 2)} {alerta.unidade_sigla}
        </span>
      </td>
      <td className="px-4 py-3 text-center">
        <button onClick={() => onEdit(alerta)} className="p-1.5 rounded hover:bg-white text-gray-500 hover:text-blue-600">
          <Edit2 size={14} />
        </button>
      </td>
    </tr>
  );
}

function AlertaConfigModal({ material, onClose }) {
  const qc = useQueryClient();
  const [obra, setObra] = useState(material.obra || '');
  const [minimo, setMinimo] = useState(material.estoque_minimo || 0);
  const [critico, setCritico] = useState(material.critico || false);

  const save = useMutation({
    mutationFn: () => api.post('/insumos/alertas/config', {
      material_id: material.material_id || material.id,
      obra: obra || null,
      estoque_minimo: minimo,
      critico,
    }),
    onSuccess: () => { qc.invalidateQueries(['insumos-alertas']); onClose(); },
  });

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
      <div className="bg-white rounded-2xl shadow-xl p-6 w-full max-w-sm">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-semibold text-gray-800">Editar Alerta</h3>
          <button onClick={onClose}><X size={18} className="text-gray-400" /></button>
        </div>
        <p className="text-sm text-gray-600 mb-4">{material.material_nome}</p>
        <label className="block text-xs text-gray-500 mb-1">Obra (deixe vazio para regra global)</label>
        <select value={obra} onChange={e => setObra(e.target.value)}
          className="w-full border rounded-lg px-3 py-2 mb-3 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400">
          <option value="">Global (todas as obras)</option>
          {OBRAS.map(o => <option key={o} value={o}>{o}</option>)}
        </select>
        <label className="block text-xs text-gray-500 mb-1">Quantidade mínima</label>
        <input type="number" min="0" step="0.01" value={minimo} onChange={e => setMinimo(e.target.value)}
          className="w-full border rounded-lg px-3 py-2 mb-3 focus:outline-none focus:ring-2 focus:ring-blue-400" />
        <label className="flex items-center gap-2 mb-4 cursor-pointer">
          <input type="checkbox" checked={critico} onChange={e => setCritico(e.target.checked)} className="w-4 h-4" />
          <span className="text-sm text-gray-700">Material crítico</span>
        </label>
        <div className="flex gap-2">
          <button onClick={onClose} className="flex-1 px-4 py-2 border rounded-lg text-sm text-gray-600">Cancelar</button>
          <button onClick={() => save.mutate()} disabled={save.isPending}
            className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium disabled:opacity-50">
            {save.isPending ? 'Salvando...' : 'Salvar'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function InsumosAlertas() {
  const [obraFiltro, setObraFiltro] = useState('');
  const [editando, setEditando] = useState(null);

  const { data, isLoading } = useQuery({
    queryKey: ['insumos-alertas', obraFiltro],
    queryFn: () => api.get('/insumos/alertas', { params: { obra: obraFiltro || undefined } }).then(r => r.data.data),
    refetchInterval: 30000,
  });

  const alertas = data || [];
  const criticos = alertas.filter(a => a.critico);
  const normais = alertas.filter(a => !a.critico);

  return (
    <div className="p-6 max-w-6xl mx-auto space-y-5">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <AlertTriangle size={22} className="text-red-500" />
          <div>
            <h1 className="text-xl font-bold text-gray-800">Alertas de Estoque</h1>
            <p className="text-sm text-gray-500">Materiais abaixo do estoque mínimo configurado</p>
          </div>
        </div>
        <select value={obraFiltro} onChange={e => setObraFiltro(e.target.value)}
          className="border rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400">
          <option value="">Todas as obras</option>
          {OBRAS.map(o => <option key={o} value={o}>{o}</option>)}
        </select>
      </div>

      {/* Sumário */}
      <div className="grid grid-cols-3 gap-4">
        <div className="bg-red-50 border border-red-100 rounded-xl p-4 text-center">
          <p className="text-3xl font-bold text-red-600">{criticos.length}</p>
          <p className="text-sm text-red-700 mt-1 flex items-center justify-center gap-1"><Shield size={13} /> Materiais críticos</p>
        </div>
        <div className="bg-yellow-50 border border-yellow-100 rounded-xl p-4 text-center">
          <p className="text-3xl font-bold text-yellow-600">{normais.length}</p>
          <p className="text-sm text-yellow-700 mt-1">Materiais abaixo do mínimo</p>
        </div>
        <div className="bg-blue-50 border border-blue-100 rounded-xl p-4 text-center">
          <p className="text-3xl font-bold text-blue-600">{alertas.length}</p>
          <p className="text-sm text-blue-700 mt-1">Total de alertas</p>
        </div>
      </div>

      {isLoading ? (
        <div className="text-center py-12 text-gray-400">Carregando alertas...</div>
      ) : alertas.length === 0 ? (
        <div className="bg-green-50 border border-green-100 rounded-xl p-10 text-center">
          <Check size={40} className="mx-auto text-green-400 mb-3" />
          <p className="text-green-700 font-medium">Todos os materiais estão acima do estoque mínimo</p>
          <p className="text-sm text-green-500 mt-1">Nenhum alerta no momento</p>
        </div>
      ) : (
        <div className="bg-white rounded-xl shadow overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-xs text-gray-500 border-b">
              <tr>
                <th className="px-4 py-3 text-left">Material</th>
                <th className="px-4 py-3 text-left">Obra</th>
                <th className="px-4 py-3 text-right">Saldo Atual</th>
                <th className="px-4 py-3 text-right">Mínimo</th>
                <th className="px-4 py-3 text-left min-w-32">Nível</th>
                <th className="px-4 py-3 text-right">Déficit</th>
                <th className="px-4 py-3 text-center">Editar</th>
              </tr>
            </thead>
            <tbody>
              {criticos.length > 0 && (
                <tr className="bg-red-100">
                  <td colSpan={7} className="px-4 py-1.5 text-xs font-semibold text-red-700 flex items-center gap-1">
                    <Shield size={12} /> MATERIAIS CRÍTICOS
                  </td>
                </tr>
              )}
              {criticos.map((a, i) => <AlertaRow key={`c-${i}`} alerta={a} onEdit={setEditando} />)}
              {normais.length > 0 && (
                <tr className="bg-yellow-100">
                  <td colSpan={7} className="px-4 py-1.5 text-xs font-semibold text-yellow-700">
                    ABAIXO DO MÍNIMO
                  </td>
                </tr>
              )}
              {normais.map((a, i) => <AlertaRow key={`n-${i}`} alerta={a} onEdit={setEditando} />)}
            </tbody>
          </table>
        </div>
      )}

      {editando && <AlertaConfigModal material={editando} onClose={() => setEditando(null)} />}
    </div>
  );
}
